// Attribution capture & propagation (COM-772) + the on-site conversion moment
// (COM-771). The real application form lives on v2.commissaryconnect.com, so
// the conversion this site can observe is the outbound apply handoff.
//
//   captureAttribution()          last-touch UTM/gclid, persisted 90 days
//   withAttribution(url)          appends the stored params to an outbound URL
//   installApplyHandoffTracking() one document-level listener that decorates
//     EVERY link to the external form at click time and fires the Google Ads
//     conversion + GA4 generate_lead — covers current and future links
//     without per-component wiring (and without double-firing: this is the
//     single place the conversion event is sent). The same listener also
//     fires the phone-click conversion for any tel: link (COM-776).

import { posthog } from './posthog';

// RELAUNCHED 2026-09-02 (Jeffrey): the site owns the intake again. The form at
// /application is the v2 replica (pages/V2FormReplica.tsx) — a proxy in front
// of v2's intake: it stores to our `applications` table first (partials
// included), then forwards to v2's AWS endpoint, which still sends the
// applicant's Mailgun confirmation. Nothing changes on v2's side.
//
// Every apply CTA keys on this constant, so attribution decoration and the
// apply-handoff conversion follow it without per-CTA changes. History: pointed
// at v2's own form 2026-08-31 → 2026-09-02 (14e038b2), during which partial
// capture went dark.
export const APPLY_FORM_URL = 'https://commissaryconnect.com/application';

// "Website lead — apply handoff (commissaryconnect.com)" — conversion action
// 7729550635 in Google Ads account 538-188-8550 (created 2026-08-21,
// secondary so it never pollutes primary conversion counts).
export const AW_CONVERSION = 'AW-828058610/wnQFCKuq3uUcEPLX7IoD';

// "Phone click" — conversion action 7729329353 (created 2026-08-21, also
// secondary). Fired for any tel: link click; per-link labels flow through
// data-phone-label.
export const AW_PHONE_CONVERSION = 'AW-828058610/AREqCMnp0OUcEPLX7IoD';
// Application submitted — the same Google Ads action v2's own form fires
// (COM-799). Lives here with the other two so every conversion id is in one
// file (CMSY-28); V2FormReplica fires it on a successful submit.
export const AW_APPLICATION_CONVERSION = 'AW-828058610/9kqKCPr14OUcEPLX7IoD';

// GA4 measurement id — the same one gtag('config')s in public/index.html.
export const GA_MEASUREMENT_ID = 'G-1TMY037LQG';

const PARAM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid'] as const;

// Bump this whenever PARAM_KEYS changes. The store is per-browser and lives for
// 90 days, so a visitor who first landed under an older key set keeps that
// shape until it expires. That already bit us once: utm_content was added to
// PARAM_KEYS after launch, and returning visitors kept handing v2 a store that
// had never contained it — which read as "the site drops utm_content" when the
// forwarding code was in fact correct. A new key retires those stores at once.
// Exported so pages/Privacy.tsx can be tested against the key this file really
// writes. The policy's section 6 lists these by name; until 2026-09-17 it named
// `cc_application_draft_v1`, a key deleted with ApplicationForm.tsx on
// 2026-09-02, and a hand-written test pinned the wrong name rather than the
// source. A disclosure that drifts is worse than none.
export const STORE_KEY = 'cc_attribution_v2';
const LEGACY_STORE_KEYS = ['cc_attribution_v1'];

const TTL_MS = 90 * 24 * 3600 * 1000;

type Stored = { at: number; params: Record<string, string> };

function read(): Stored | null {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Stored;
    if (!s?.at || Date.now() - s.at > TTL_MS) return null;
    return s;
  } catch {
    return null;
  }
}

// LAST touch wins, deliberately. Google Ads attributes a conversion to the LAST
// ad click, so storing the first one guaranteed our application records and the
// ad account would name different campaigns for the same lead — with no way to
// tell which was which after the fact. Matching Google keeps one story.
//
// Note this only overwrites on a visit that actually carries tracking params: an
// untagged return visit (direct, organic, a bookmark) leaves the stored
// attribution alone, so a lead is never silently reassigned to "no source".
//
// To go back to first-touch, restore the `if (read()) return;` guard below — and
// expect the permanent disagreement with Google's numbers that comes with it.
export function captureAttribution(): void {
  try {
    for (const k of LEGACY_STORE_KEYS) localStorage.removeItem(k);

    const qs = new URLSearchParams(window.location.search);
    const params: Record<string, string> = {};
    for (const k of PARAM_KEYS) {
      const v = qs.get(k);
      if (v) params[k] = v;
    }
    if (Object.keys(params).length === 0) return;
    localStorage.setItem(STORE_KEY, JSON.stringify({ at: Date.now(), params }));
  } catch {
    // storage unavailable (private mode etc.) — degrade silently
  }
}

export function getAttribution(): Record<string, string> {
  return read()?.params ?? {};
}

// ── first-touch landing context (CMSY-67) ───────────────────────────────────
//
// The ad-param store above only writes when a URL carries utm_*/gclid, which
// is exactly the visits the ads bring. Everyone else — organic Google, a typed
// address, the Maps listing — stored nothing, so on 9 Sep 2026 five of six real
// applications reached the row as "unattributed" and it took a hand query
// against PostHog's person properties to learn that three were organic search
// and two direct. This store remembers the FIRST arrival of a browser: the
// referrer host (or 'direct'), the landing path and when. First touch, on
// purpose, and separate from the last-touch ad params: "how did they find us"
// is a different question from "which ad gets the credit", and the two must
// never overwrite each other.
//
// These keys ride inside the `attribution` object the form posts to
// /api/apply, which stores it verbatim and never forwards it to v2. They are
// NOT appended to outbound URLs — withAttribution() stays ad-params only.
export const LANDING_KEY = 'cc_landing_v1';
export const LANDING_TTL_MS = TTL_MS;

export type Landing = { ref: string; landing_path: string; landed_at: string };

function referrerHost(): string {
  try {
    const r = document.referrer;
    if (!r) return 'direct';
    const host = new URL(r).hostname.toLowerCase();
    // Our own pages are not a source; a same-site referrer means a reload or a
    // navigation that lost SPA state, not an arrival.
    if (!host || host === window.location.hostname.toLowerCase()) return 'direct';
    return host;
  } catch {
    return 'direct';
  }
}

export function captureLanding(): void {
  try {
    if (getLanding()) return; // first touch: never overwritten while it lives
    const landing: Landing = {
      ref: referrerHost(),
      landing_path: (window.location.pathname || '/').slice(0, 200),
      landed_at: new Date().toISOString(),
    };
    localStorage.setItem(LANDING_KEY, JSON.stringify({ at: Date.now(), landing }));
  } catch {
    // storage unavailable — degrade silently, the row just carries no landing
  }
}

export function getLanding(): Landing | null {
  try {
    const raw = localStorage.getItem(LANDING_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as { at?: number; landing?: Landing };
    if (!s?.at || Date.now() - s.at > LANDING_TTL_MS) return null;
    const l = s.landing;
    if (!l || typeof l.ref !== 'string' || typeof l.landing_path !== 'string') return null;
    return l;
  } catch {
    return null;
  }
}

/**
 * What the application row records about where this person came from: the
 * last-touch ad params plus the first-touch landing. This — not
 * getAttribution() — is what the form sends.
 */
export function applicationAttribution(): Record<string, string> {
  return { ...(getLanding() ?? {}), ...getAttribution() };
}

export function withAttribution(url: string): string {
  const entries = Object.entries(getAttribution());
  if (entries.length === 0) return url;
  try {
    const u = new URL(url);
    for (const [k, v] of entries) if (!u.searchParams.has(k)) u.searchParams.set(k, v);
    return u.toString();
  } catch {
    return url;
  }
}

// ── page_view on client-side navigation (COM-1029) ─────────────────────────
//
// index.html's gtag('config') sends exactly ONE page_view: the initial HTML
// load. Every route change after that is a React render, so GA4 was being sent
// custom events (view_facility, the click events, scroll) for pages it had
// never seen a page_view for. The site measured 0.71 page_views per session —
// impossible for a real visit, since loading a page produces at least one —
// and ~30% of sessions arrived with no session_start and therefore no traffic
// source ("Unassigned").
//
// Two guarantees:
//   firePageView()   one page_view per client-side route change. NOT for the
//                    initial load: gtag('config') already sent that one, and a
//                    second would double-count every landing.
//   afterPageView()  defers an event until the current route's page_view has
//                    gone out. A page's mount effect runs before the router-level
//                    tracker's effect, so view_facility would otherwise reach
//                    Google ahead of the page_view it belongs to.

// The path GA4 has already received a page_view for. The initial load is
// covered by config, so it starts as the landing path.
let pageViewPath: string | null =
  typeof window === 'undefined' ? null : window.location.pathname;
let deferredEvents: Array<() => void> = [];

export function firePageView(path: string): void {
  pageViewPath = path;
  // GA4 only. The Ads tag's own page hit comes from its config call; sending a
  // second one here would change remarketing counts, which is not this fix.
  window.gtag?.('event', 'page_view', {
    send_to: GA_MEASUREMENT_ID,
    page_location: window.location.href,
    page_path: path,
    page_title: document.title,
  });
  const pending = deferredEvents;
  deferredEvents = [];
  for (const fn of pending) fn();
}

export function afterPageView(fn: () => void): void {
  if (pageViewPath === window.location.pathname) {
    fn();
    return;
  }
  deferredEvents.push(fn);
}

export function fireApplyHandoffConversion(label: string): void {
  // PostHog mirror (COM-907) — observation only, same single firing point.
  try {
    // sendBeacon, because this click NAVIGATES: an ordinary capture races the
    // unload and loses (CMSY-55 — measured on production, the same click fired
    // the event only when navigation was prevented). Apply-click rate is THE
    // GOAL metric #1, so a lost event is a understated number, not a rounding.
    if (posthog.__loaded) posthog.capture('apply_handoff', { label }, { transport: 'sendBeacon' });
  } catch {
    // posthog unavailable (tests, dev) — degrade silently
  }
  if (!window.gtag) return;
  window.gtag('event', 'conversion', { send_to: AW_CONVERSION });
  window.gtag('event', 'generate_lead', { event_category: 'Application', event_label: label });
}

export function firePhoneClickConversion(label: string): void {
  // PostHog mirror (CMSY-30) — before the gtag guard, so a blocked or missing
  // Google tag does not also cost us the PostHog record. apply_handoff was
  // PostHog's only CTA event, which left recordings and heatmaps unable to see
  // phone intent at all.
  try {
    // Same reason: a tel: click hands off to the dialer and can unload the page.
    if (posthog.__loaded) posthog.capture('phone_call_click', { label, pathname: window.location.pathname }, { transport: 'sendBeacon' });
  } catch {
    // posthog unavailable (tests, dev) — degrade silently
  }
  if (!window.gtag) return;
  window.gtag('event', 'conversion', { send_to: AW_PHONE_CONVERSION });
  window.gtag('event', 'phone_call_click', { event_label: label });
}

/**
 * Which facility page an ad click actually landed on (CMSY-30). GA4 has had
 * this since COM-773; PostHog had nothing, so a session replay could not be
 * joined to the facility the visitor was looking at. GA4's own event stays
 * exactly where it is, fired by the page's deferred effect.
 */
export function fireFacilityView(slug: string, v2FacilityId?: number): void {
  try {
    if (posthog.__loaded) posthog.capture('view_facility', { slug, v2_facility_id: v2FacilityId ?? null });
  } catch {
    // posthog unavailable (tests, dev) — degrade silently
  }
}

const squash = (s: string | null | undefined): string => (s ?? '').replace(/\s+/g, ' ').trim();

/**
 * The label that says WHICH call-to-action was clicked, for a CTA that carries
 * no explicit `data-apply-label` / `data-phone-label`.
 *
 * It must never be the event's own name: one in four of the first
 * `apply_handoff` events arrived labelled `apply_handoff` (CMSY-18), which
 * attributes to no button at all — the header's Apply link is on every page and
 * had no label, so /our-kitchens reported the event name. The fallback names the
 * page and the section or link text instead, so a missing attribute degrades to
 * a usable label rather than an unusable one. Add the attribute anyway; this is
 * the safety net, not the plan.
 */
export function ctaFallbackLabel(el: Element, kind: string): string {
  const path = window.location.pathname.replace(/(.)\/$/, '$1');
  const section =
    squash(el.closest('[data-section]')?.getAttribute('data-section')) ||
    squash(el.closest('section, article, header, footer, main')?.querySelector('h1, h2, h3')?.textContent);
  return `${path}:${(section || squash(el.textContent) || kind).slice(0, 40)}`;
}

let installed = false;
export function installApplyHandoffTracking(): void {
  if (installed) return;
  installed = true;
  document.addEventListener(
    'click',
    (e) => {
      const el = e.target as HTMLElement | null;
      const a = el?.closest?.('a');
      if (!a?.href) return;
      if (a.href.startsWith('tel:')) {
        firePhoneClickConversion(squash(a.dataset.phoneLabel) || ctaFallbackLabel(a, 'phone'));
        return;
      }
      if (!a.href.startsWith(APPLY_FORM_URL)) return;
      a.href = withAttribution(a.href); // decorate before the browser follows it
      fireApplyHandoffConversion(squash(a.dataset.applyLabel) || ctaFallbackLabel(a, 'apply'));
    },
    true,
  );
}
